import React from 'react';
import { useController } from 'react-hook-form';
import Big from 'big.js';
import AmountInput from '@alfa-retail-react/components/amount-input';
import { useFormContext } from '@alfa-retail-react/components';

import { WEB_VISOR_HIDE } from 'app/utils/AnalyticsUtils';
import { FormatUtils } from 'app/utils/FormatUtils';
import { ValidationRules } from 'app/form/ValidationRules';
import { ProductParametersConfigureFormValues } from 'app/logic/pages/productParametersConfigure/ProductParametersConfigurePage';
import { useI18nContext } from '../../../store/сontext/I18nContext';

export interface LimitFormItemProps {
    /**
     * Минимально возможное значение лимита.
     */
    minAmount: Big;

    /**
     * Максимально возможное значение лимита.
     */
    maxAmount: Big;

    /**
     * Значение лимита
     */
    value: string;

    /**
     * Валюта лимита
     */
    currency: string;

    /**
     * Setter значения лимита.
     */
    setValue: Function;
}

/**
 * Компонент формы для выбора лимита по кредиту
 */
export const LimitFormItem = ({
    minAmount,
    maxAmount,
    value,
    currency,
    setValue,
}: LimitFormItemProps) => {
    const { t } = useI18nContext() as { t: (key: string | string[]) => string };

    const min = minAmount.toNumber();
    const max = maxAmount.toNumber();
    const label = t([
        'creditCard_terms_desiredLimit_range',
        FormatUtils.toIntegerString(min),
        FormatUtils.toIntegerString(max),
        currency,
    ]);
    const minError = t([
        'creditCalculator_panel_amount_min',
        FormatUtils.toIntegerString(min),
        currency,
    ]);
    const maxError = t([
        'creditCalculator_panel_amount_max',
        FormatUtils.toIntegerString(max),
        currency,
    ]);

    const handleInputChange = ({ value: val }: { value: number | '' }) => {
        setValue(val !== '' ? (+val / 100).toString() : '');
    };

    const LIMIT_FORM_ITEM_NAME = 'limit';
    const { control, handleMultipleErrors, setFieldElement } =
        useFormContext<ProductParametersConfigureFormValues>();
    const { field, fieldState } =
        useController<ProductParametersConfigureFormValues>({
            name: LIMIT_FORM_ITEM_NAME,
            control,
            defaultValue: Number(value),
            rules: {
                required: ValidationRules.required(),
                min: ValidationRules.min(Number(min), minError),
                max: ValidationRules.max(Number(max), maxError),
            },
        });

    return (
        <AmountInput
            bold
            integersOnly
            ref={(element) => {
                field.ref(element);
                setFieldElement(LIMIT_FORM_ITEM_NAME, element);
            }}
            className={WEB_VISOR_HIDE}
            label={label}
            suffix={currency}
            value={Number(value) * 100}
            size="xl"
            error={handleMultipleErrors(fieldState.error)}
            onBlur={field.onBlur}
            onChange={(event, payload) => {
                const result =
                    typeof payload.value === 'string'
                        ? ''
                        : (payload.value as number) / 100;
                field.onChange(result);
                handleInputChange(payload);
            }}
            onClear={() => {
                handleInputChange({ value: '' });
                field.onChange('');
            }}
        />
    );
};
